import type { QueryClient } from '@tanstack/react-query';
import { Outlet, createRootRouteWithContext, useRouter } from '@tanstack/react-router';
import { Button, EmptyState, ErrorState, Toaster, TooltipProvider } from '@stayafrica/ui';
import { FileQuestion } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { errorMessage, errorTraceId } from '@/lib/errors';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: RootError,
});

function RootLayout() {
  return (
    <TooltipProvider delayDuration={300}>
      <Outlet />
      <Toaster />
    </TooltipProvider>
  );
}

function NotFound() {
  const { t } = useTranslation(['common']);
  const router = useRouter();
  return (
    <div className="main">
      <EmptyState
        icon={<FileQuestion className="ico ico-32" />}
        title={t('common:states.notFoundTitle')}
        description={t('common:states.notFoundDescription')}
        action={
          <Button variant="primary" onClick={() => void router.navigate({ to: '/' })}>
            {t('common:actions.back')}
          </Button>
        }
      />
    </div>
  );
}

function RootError({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation(['common', 'errors']);
  return (
    <div className="main">
      <ErrorState
        title={t('common:states.errorTitle')}
        description={errorMessage(error, t)}
        traceId={errorTraceId(error)}
        retryLabel={t('common:actions.retry')}
        onRetry={reset}
        traceCopyLabel={t('common:actions.copy')}
        traceCopiedLabel={t('common:actions.copied')}
      />
    </div>
  );
}
